import React from 'react';

/**
 * The words inside every (i) in the app, in one place.
 *
 * Each export is what one InfoBubble shows, and its label is what a screen
 * reader calls the (i). Kept together so the copy can be read as a set - the
 * bubbles are the only place most of these rules are told to the user, and
 * a rule told two different ways in two places is worse than none.
 *
 * Every claim in here is something the code actually does. When the code
 * changes, the sentence changes with it; a bubble that lies is worse than no
 * bubble.
 */

export const storageProviderInfo = {
  label: 'What a storage provider is',
  text: (
    <>
      <p>
        <b>One account holds your library.</b> Fadila keeps its index - which tracks you have added,
        your playlists, your settings - in a small private app folder of the account you pick. The
        music itself stays wherever it already is.
      </p>
      <p>
        The other services stay available as places to browse and play from; they just do not hold
        the index. Only one account holds it at a time.
      </p>
      <p>
        Switching later restarts Fadila with the other account's library and copies nothing across.
        Two accounts are two separate libraries.
      </p>
      <p>
        Google is asked only for the app-folder permission here. Reading your Drive comes later,
        when you first browse it.
      </p>
    </>
  ),
};

export const googleSessionInfo = {
  label: 'About the Google sign-in',
  text: (
    <>
      <p>
        <b>Signing in takes you to Google and back.</b> Google asks you to confirm Fadila's
        permissions every time, because that is what lets Fadila stay signed in afterwards: access
        is renewed quietly in the background, on every device, for as long as you leave Fadila
        connected to your account.
      </p>
      <p>
        Logout here withdraws Fadila's access at Google, the same as removing it from your Google
        account's permissions page. "Authorisation expired" means the renewal has stopped working -
        signing in again fixes it.
      </p>
    </>
  ),
};

export const syncInfo = {
  label: 'What Synchronize does',
  text: (
    <>
      <p>
        <b>Pulls the linked playlist in now</b>, and pushes your changes back out where the link's
        direction allows it. It also runs on its own when you open this playlist or the service's
        page, and shortly after you edit it; podcasts pull once a day.
      </p>
      <p>
        With a one-way link <i>from</i> the service, a track you remove here is added back on the
        next sync. Set the direction in the service's submenu, or unlink there.
      </p>
      <p>
        A track the service reports gone is skipped from then on; "Retry skipped" in the submenu
        gives it one more try.
      </p>
    </>
  ),
};

export const refetchTagsInfo = {
  label: 'What Refetch Tags does',
  text: (
    <>
      <p>
        <b>Reads the tags in the audio file again</b> and fills in anything missing or changed:
        title, artist, album, genre and year, plus album artist, composer, comment, track and disc
        number, BPM, key and publisher.
      </p>
      <p>
        It also reads what the file is - its format, from the file name, and its bit rate, from the
        file's own headers - for the Format and Bit Rate columns. A track added before those columns
        existed shows them blank until it is refetched.
      </p>
      <p>
        Fields you set by hand in "Edit info" are kept. To let the file win again, use "Discard my
        edits and refetch".
      </p>
      <p>
        Needs the file's service signed in. For a YouTube track only the length is refreshed; there
        are no tags to read.
      </p>
      <p>
        A file that cannot be read is marked missing, and a successful refetch clears the mark. So
        this is also the way to check whether a track is still there, and the way to clear a mark
        that was wrong.
      </p>
    </>
  ),
};

export const savePodcastInfo = {
  label: 'What Save to library does',
  text: (
    <>
      <p>
        <b>Downloads this episode into your own drive</b> - the podcast save path under Settings →
        Advanced, OneDrive only for now - and swaps the episode for the saved file in every playlist
        that has it.
      </p>
      <p>From then on it plays from your drive and stays after the feed drops it.</p>
    </>
  ),
};

export const spotifyInfo = {
  label: 'What Spotify needs',
  text: (
    <>
      <p>
        <b>Spotify Premium, on the account you sign in with.</b> Spotify's web player refuses to
        play without it, and a mobile-only Premium plan does not count.
      </p>
      <p>
        Playback runs inside this tab through Spotify's own player, so it plays here and not on your
        other Spotify devices.
      </p>
      <p>
        Fadila is a small app in Spotify's Development Mode: only accounts the app owner has listed
        can sign in, and search returns ten results at a time.
      </p>
    </>
  ),
};

export const mixcloudInfo = {
  label: 'About Mixcloud in Fadila',
  text: (
    <>
      <p>
        <b>No account needed.</b> Search shows or people, or paste a mixcloud.com link.
      </p>
      <p>
        Playback is Mixcloud's own embedded player: the bar that appears in the player is theirs,
        with their play button and scrubber, and it has to stay on screen to work. Shows that are
        subscriber-only on Mixcloud will not play.
      </p>
      <p>
        Mixcloud has no artist field, so a show is credited to the account that posted it. Settings
        → Advanced can guess the DJ from the title instead.
      </p>
    </>
  ),
};

export const resetAppDataInfo = {
  label: 'What resetting app data does',
  text: (
    <>
      <p>
        <b>Deletes Fadila's private folder in this Google Drive</b> - the library index, every
        playlist and your settings - and starts over empty. No music file is touched.
      </p>
      <p>There is no undo. "Download GoogleDrive AppData" first keeps a copy.</p>
    </>
  ),
};

export const revokePermissionsInfo = {
  label: 'What revoking permissions does',
  text: (
    <>
      <p>
        <b>Withdraws every permission Fadila holds on this Google account</b>, Drive and YouTube
        alike, and signs it out. The next sign-in asks for consent again, which is how you move
        Fadila to another Google account or YouTube channel.
      </p>
      <p>
        Nothing in Drive is deleted; the app folder is still there when that account signs in again.
      </p>
    </>
  ),
};

export const streamingInfo = {
  label: 'About streaming accounts',
  text: (
    <>
      <p>
        <b>Accounts for playing and browsing.</b> None of these holds your library; that is the
        storage account above.
      </p>
      <p>
        YouTube is not listed here because it uses the Google account under Storage, whether or not
        Google is holding the library. Mixcloud needs no account at all.
      </p>
    </>
  ),
};

export const saveLibraryInfo = {
  label: 'What Save Library does',
  text: (
    <>
      <p>
        <b>Writes the library to your storage account now.</b> Changes are otherwise written on a
        timer - five minutes after the last one for the library, fifteen seconds for playlists - and
        when you leave the page.
      </p>
      <p>
        Worth a press before closing the app on an iPhone or iPad, where the leaving-the-page write
        does not get to run.
      </p>
    </>
  ),
};

export const itunesImportInfo = {
  label: 'How the iTunes import finds your tracks',
  text: (
    <>
      <p>
        <b>
          Rebuilds playlists from an iTunes <code>Library.xml</code>
        </b>{' '}
        by looking each track up in the library you already have. Shows you what it found and what
        it could not place before writing anything.
      </p>
      <p>
        <b>iTunes and Fadila share no track ids</b>, and the file paths in an iTunes library point
        at a machine you may not even own any more. So tracks are matched on what both sides read
        out of the same audio files: artist, album and title, with the track length used to break a
        tie between two takes of the same name.
      </p>
      <p>
        Matching is exact on those fields, never fuzzy. A track reported as not found is better than
        a wrong one added quietly to a playlist you spent months curating - and you get the list of
        what it could not place, so you can go and look.
      </p>
      <p>
        Two kinds of doubt get their own row in the report, and neither is imported by default.{' '}
        <b>Uncertain</b> means the artist, album and title match but no track has the right length:
        the closest is seconds out while everything else is far off, so it is probably another rip
        or a trimmed copy. The row says how far out and what it would add, and a checkbox takes them
        all if you want them. <b>Ambiguous</b> means several tracks share the artist, album and
        title and the lengths cannot separate them - a white label whose cuts are all tagged the
        same. Those are never imported; there is nothing to choose between. The exception is marked{' '}
        <b>copies</b>: the same recording is in your library more than once, with lengths a hair
        apart, so any of them is the right music. A second checkbox adds one copy for each of those.
      </p>
      <p>Neither is a duplicate in your library, which cannot hold the same cloud file twice.</p>
      <p>
        <b>Only tracks already in your Fadila library can be added.</b> A library track is a file in
        your cloud drive, and a path from an old computer cannot be turned into one. Add the folder
        to Fadila first, then import.
      </p>
      <p>
        Playlists are written to your storage account as soon as you import. Nothing else is touched
        - not the library, not the audio files, and not the playlists you did not pick.
      </p>
    </>
  ),
};

export const downloadAppDataInfo = {
  label: 'What downloading app data does',
  text: (
    <>
      <p>
        <b>Saves a copy of Fadila's private folder to this device</b> - the library index, playlists
        and settings, as the gzipped JSON files they are. A backup, and the way to look inside them.
      </p>
    </>
  ),
};

export const podcastPathInfo = {
  label: 'About the podcast save path',
  text: (
    <>
      <p>
        <b>Where "Save to library" puts a podcast episode.</b> OneDrive only for now, and the
        folders have to exist already.
      </p>
      <p>
        See{' '}
        <a
          href="https://developers.google.com/drive/api/v3/about-files"
          target="_blank"
          rel="noopener noreferrer"
        >
          the Google Drive API docs
        </a>{' '}
        for what a file or folder id is; both are visible from Google Drive itself.
      </p>
    </>
  ),
};

export const localStorageInfo = {
  label: 'About storing the library in this browser',
  text: (
    <>
      <p>
        <b>Development builds only</b> - this is not in a deployed Fadila.
      </p>
      <p>
        Keeps the library in this browser's localStorage instead of a cloud account: nothing syncs,
        nothing leaves this machine, and the ~5 MB quota is the ceiling. It exists so the browser
        tests can reach the app without signing in to anything (see e2e/README.md), and switching
        reloads the app.
      </p>
    </>
  ),
};
